/**
 * Operator cross-app handoff mailbox — MCP dc__ops_handoff_list / dc__ops_handoff_post
 */

import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Archive, Loader2, Send } from 'lucide-react';
import PageShell from '@/components/PageShell';
import { callMcpTool } from '@/lib/mcpToolClient';

const OPS_APP_KEY = 'dc.ops_handoff.app_id';
const OPS_MCP_KEY = 'dc.ops_handoff.mcp_key';

function loadStored(key: string, fallback: string) {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

export default function OpsHandoffPage() {
  const [opsAppId, setOpsAppId] = useState(() => loadStored(OPS_APP_KEY, 'devcockpit'));
  const [mcpKey, setMcpKey] = useState(() => loadStored(OPS_MCP_KEY, ''));
  const [targetAppId, setTargetAppId] = useState('');
  const [message, setMessage] = useState('');
  const [saved, setSaved] = useState(false);

  function persistCredentials() {
    try {
      localStorage.setItem(OPS_APP_KEY, opsAppId);
      localStorage.setItem(OPS_MCP_KEY, mcpKey);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      // ignore
    }
  }

  const listQuery = useQuery({
    queryKey: ['ops-handoff', opsAppId, targetAppId],
    queryFn: async () => {
      if (!mcpKey || !targetAppId) return { notes: [] };
      const { data } = await callMcpTool<any>(opsAppId, mcpKey, 'dc__ops_handoff_list', {
        app_id: targetAppId,
        limit: 30,
      });
      return data;
    },
    enabled: Boolean(mcpKey && targetAppId && opsAppId),
    retry: false,
  });

  const postMutation = useMutation({
    mutationFn: async () => {
      const { data } = await callMcpTool(opsAppId, mcpKey, 'dc__ops_handoff_post', {
        app_id: targetAppId,
        message: message.trim(),
        tags: ['operator-ui'],
      });
      return data;
    },
    onSuccess: () => {
      setMessage('');
      listQuery.refetch();
    },
  });

  const notes = listQuery.data?.notes ?? [];

  return (
    <PageShell
      title="Ops handoff mailbox"
      subtitle="Cross-app operator notes via MCP (requires platform:ops scoped MCP key)"
      icon={Archive}
      iconColor="bg-slate-700"
    >
      <div className="space-y-6 max-w-3xl">
        <Card>
          <CardContent className="p-4 space-y-3">
            <p className="text-sm text-gray-600">
              Use an ops-capable app MCP key (platform:ops scope). Credentials are stored in this browser only.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label className="text-xs text-gray-500">Ops MCP app id</label>
                <Input value={opsAppId} onChange={(e) => setOpsAppId(e.target.value)} className="mt-1" />
              </div>
              <div>
                <label className="text-xs text-gray-500">MCP key</label>
                <Input
                  type="password"
                  value={mcpKey}
                  onChange={(e) => setMcpKey(e.target.value)}
                  className="mt-1"
                  placeholder="mcp_…"
                />
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={persistCredentials}>
              {saved ? 'Saved' : 'Save credentials'}
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <label className="text-xs text-gray-500">Target app id</label>
            <Input
              value={targetAppId}
              onChange={(e) => setTargetAppId(e.target.value)}
              placeholder="app_…"
            />
            {listQuery.isFetching && (
              <p className="text-sm text-gray-500 flex items-center gap-1">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            )}
            {listQuery.isError && (
              <p className="text-sm text-red-700">{(listQuery.error as Error)?.message}</p>
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {notes.length === 0 && targetAppId && !listQuery.isFetching && (
                <p className="text-sm text-gray-500">No handoff notes for this app.</p>
              )}
              {notes.map((note: any, i: number) => (
                <div key={i} className="border rounded-md p-3 bg-gray-50 text-sm">
                  <div className="flex gap-2 flex-wrap mb-1">
                    {note.timestamp && (
                      <span className="text-[10px] text-gray-500">
                        {new Date(note.timestamp).toLocaleString()}
                      </span>
                    )}
                    {(note.tags ?? []).map((t: string) => (
                      <Badge key={t} variant="outline" className="text-[10px]">
                        {t}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-gray-800 whitespace-pre-wrap">{note.message}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Post an operator handoff note…"
              rows={4}
            />
            <Button
              disabled={!message.trim() || !targetAppId || !mcpKey || postMutation.isPending}
              onClick={() => postMutation.mutate()}
            >
              {postMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-1" />
              )}
              Post note
            </Button>
            {postMutation.isError && (
              <p className="text-sm text-red-700">{(postMutation.error as Error)?.message}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </PageShell>
  );
}
